// BugReportForm — modal that posts to support_tickets (mig 443).
//
// Renders a small form: category radio, severity select, title +
// description. Auto-captures page_url, user_agent, app_version and the
// reporter's identity (snapshotted so the row survives the user being
// soft-deleted later). Submits via plain INSERT — tenant_gate scopes
// the row to the caller's active tenant, the INSERT policy enforces
// reporter_user_id = auth.uid().
//
// Layout: the shared <Modal/> shell (UX-audit §5.3) — focus trap, Escape,
// dirty guard and footer all come from there.
//
// REACH (2026-07-15): this form used to be reachable only via <HelpButton/>,
// which is mounted on 5 of 228 routes — so in ~2 months it collected exactly
// ZERO tickets. It is now opened globally via openBugReport() from the ⌘K
// palette's Actions group, mirroring openPalletSearch(). <BugReportRoot/> is
// mounted once in App.tsx and owns the only instance; HelpButton delegates to
// the same opener rather than rendering its own copy.

import { useState, useEffect } from 'react'
import { AlertCircle, Bug, Lightbulb, HelpCircle as HelpIcon, Send } from 'lucide-react'
import clsx from 'clsx'
import { supabase } from '@/lib/supabase'
import { sessionId } from '@/lib/errorReporter'
import { useCurrentUser } from '@/lib/useCurrentUser'
import Modal from '@/components/Modal'

const OPEN_EVENT = 'kf49:bug-report-open'

/** Opens the support-ticket modal from anywhere (⌘K palette, help drawer). */
export function openBugReport() {
  window.dispatchEvent(new CustomEvent(OPEN_EVENT))
}

/**
 * Mount ONCE (App.tsx). Owns the single global instance so every entry point
 * opens the same modal — no duplicate mounts, no per-page wiring.
 */
export function BugReportRoot() {
  const [open, setOpen] = useState(false)
  useEffect(() => {
    const onOpen = () => setOpen(true)
    window.addEventListener(OPEN_EVENT, onOpen)
    return () => window.removeEventListener(OPEN_EVENT, onOpen)
  }, [])
  // Mount = open (the shell's idiom): the modal's focus capture, focus restore
  // and dirty baseline all seed on mount, so it must not exist while closed.
  // Unmounting is also what resets the fields between openings.
  if (!open) return null
  return <BugReportForm onClose={() => setOpen(false)} />
}

type Category = 'BUG' | 'QUESTION' | 'FEATURE_REQUEST' | 'OTHER'
type Severity = 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT'

const CATEGORIES: { value: Category; label: string; Icon: typeof Bug }[] = [
  { value: 'BUG',             label: 'Bug',              Icon: Bug         },
  { value: 'FEATURE_REQUEST', label: 'Feature request',  Icon: Lightbulb   },
  { value: 'QUESTION',        label: 'Question',         Icon: HelpIcon    },
  { value: 'OTHER',           label: 'Other',            Icon: AlertCircle },
]

const DEFAULT_CATEGORY: Category = 'BUG'
const DEFAULT_SEVERITY: Severity = 'NORMAL'
/** The mount-time dirty baseline — see `snapshot` below. */
const CLEAN_SNAPSHOT = `${DEFAULT_CATEGORY}|${DEFAULT_SEVERITY}||`

interface Props {
  onClose: () => void
}

// Module-private on purpose: <BugReportRoot/> above owns the only instance, and
// the public API of this file is openBugReport() + BugReportRoot. Exporting the
// modal too would invite a second mount — which is precisely the per-page wiring
// that left this form reachable from 5 of 228 routes. (The fallow gate caught the
// now-unused default export; removed rather than suppressed.)
function BugReportForm({ onClose }: Props) {
  const { data: me } = useCurrentUser()
  const [category, setCategory] = useState<Category>(DEFAULT_CATEGORY)
  const [severity, setSeverity] = useState<Severity>(DEFAULT_SEVERITY)
  const [title,    setTitle]    = useState('')
  const [body,     setBody]     = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error,    setError]    = useState<string | null>(null)
  const [success,  setSuccess]  = useState(false)

  const submit = async () => {
    if (!title.trim()) { setError('Please add a short title.'); return }
    if (title.trim().length < 3) { setError('Title needs to be at least 3 characters.'); return }
    if (!me) { setError('Not signed in — refresh and try again.'); return }
    setSubmitting(true); setError(null)

    const { data: { session } } = await supabase.auth.getSession()
    const email = session?.user?.email ?? null

    const { error } = await supabase.from('support_tickets').insert({
      reporter_user_id: me.id,
      reporter_name:    me.name,
      reporter_email:   email,
      category,
      severity,
      title:       title.trim(),
      body:        body.trim(),
      page_url:    typeof window !== 'undefined' ? window.location.href : null,
      user_agent:  typeof navigator !== 'undefined' ? navigator.userAgent : null,
      app_version: (import.meta.env.VITE_APP_VERSION as string | undefined) ?? null,
      // errorReporter's per-tab id — /support joins this to client_errors so
      // triage sees the errors captured in the same session as the ticket.
      session_id:  sessionId(),
    })
    setSubmitting(false)
    if (error) { setError(error.message); return }
    setSuccess(true)
  }

  // Dirty baseline (K-forms #5): every field, synchronous at mount, so a stray
  // backdrop tap can't discard a half-typed ticket. Once the ticket is SENT the
  // fields stay populated behind the thank-you panel but are no longer unsaved
  // work — collapse the snapshot back to the mount-time baseline so closing an
  // already-submitted ticket never asks "Discard changes?".
  const snapshot = success ? CLEAN_SNAPSHOT : `${category}|${severity}|${title}|${body}`

  return (
    <Modal
      onClose={onClose}
      snapshot={snapshot}
      onSubmit={() => { void submit() }}
      isPending={submitting}
      size="lg"
      title={
        <span className="flex items-center gap-2">
          <Send size={18} className="text-blue-400 shrink-0" />
          <span className="truncate">Open a support ticket</span>
        </span>
      }
      footer={attemptClose => (success ? null : (
        // No footer once sent — the thank-you panel carries its own Close.
        <div className="px-4 py-3 border-t hairline flex items-center justify-end gap-2 shrink-0">
          <button type="button" onClick={attemptClose} className="btn-ghost">Cancel</button>
          <button
            type="submit"
            disabled={submitting || !title.trim()}
            className="btn-primary flex items-center gap-1.5">
            <Send size={14} /> {submitting ? 'Sending…' : 'Send'}
          </button>
        </div>
      ))}>
        {success ? (
          <div className="py-2 text-center space-y-3">
            <p className="text-base text-white light:text-fg">Thanks — ticket submitted.</p>
            {/* Says only what is TRUE. The previous copy promised "we get a
                notification and will be in touch" and "you can see the ticket
                in your team's queue" — mig 443 has no notify trigger and no
                tenant-facing queue exists, so both were false. Support now
                reads a platform-admin triage queue (/support); there is no
                push/email on insert by design (ticket volume is ~0). If a
                notify is ever added, say so here — never the reverse. */}
            <p className="text-sm text-gray-400">
              It's in the support queue with everything we need to reproduce it.
              We review new tickets regularly — urgent ones first.
            </p>
            <p className="text-xs text-gray-500">
              If it's stopping you packing or dispatching right now, phone it in
              as well — don't wait on this.
            </p>
            {/* The shell's single closer is the dirty guard, and a SENT ticket
                has nothing left to discard (the snapshot above is back at the
                baseline) — so this panel's own Close goes straight to onClose. */}
            <button type="button" onClick={onClose} className="btn-primary mt-4">Close</button>
          </div>
        ) : (
          <div className="space-y-4 text-sm">
            <div>
              <label className="text-xs uppercase tracking-wider text-gray-500">Type</label>
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                {CATEGORIES.map(({ value, label, Icon }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setCategory(value)}
                    className={clsx(
                      'flex items-center gap-2 rounded-lg px-3 py-2 border text-sm transition-colors',
                      category === value
                        ? 'border-blue-500 bg-blue-500/15 text-white'
                        : 'hairline-strong hover:border-gray-600 text-gray-300 light:text-fg-secondary',
                    )}>
                    <Icon size={14} /> {label}
                  </button>
                ))}
              </div>
            </div>

            <label className="block">
              <span className="block text-xs uppercase tracking-wider text-gray-500">Severity</span>
              <select
                value={severity}
                onChange={e => setSeverity(e.target.value as Severity)}
                className="input mt-1.5">
                <option value="LOW">Low — small annoyance</option>
                <option value="NORMAL">Normal</option>
                <option value="HIGH">High — blocking part of my work</option>
                <option value="URGENT">Urgent — can't pack / dispatch</option>
              </select>
            </label>

            <label className="block">
              <span className="block text-xs uppercase tracking-wider text-gray-500">Title</span>
              <input
                value={title}
                onChange={e => setTitle(e.target.value)}
                maxLength={200}
                placeholder="e.g. Pallet weight not saving on tipping"
                className="input mt-1.5"
              />
            </label>

            <label className="block">
              <span className="block text-xs uppercase tracking-wider text-gray-500">
                What happened? (optional)
              </span>
              <textarea
                value={body}
                onChange={e => setBody(e.target.value)}
                maxLength={5000}
                rows={5}
                placeholder="What were you trying to do, what went wrong, and what did you expect to happen?"
                className="input mt-1.5 resize-y"
              />
            </label>

            {error && (
              <p className="text-sm text-red-400 bg-red-900/30 border border-red-900 rounded-lg px-3 py-2">
                {error}
              </p>
            )}

            <p className="text-[11px] text-gray-500 pt-1">
              We snapshot the current page URL, your browser, and app version so
              we can reproduce the issue. No screenshots are uploaded.
            </p>
          </div>
        )}
    </Modal>
  )
}
